import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { UsersService } from '../../../services/users/users.service';
import { SnackBarComponent } from '../../snackbar/snackbar.component';

@Component({
    selector: 'app-edituseremaildialog',
    templateUrl: './edituseremaildialog.component.html',
    styleUrls: [
        './edituseremaildialog.component.scss',
        '../../../directives/mat-basic-spinner.directive.scss',
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditUserEmailDialogComponent {
  public form;
  public saving = false;
  public emailVerified: boolean;
  public currentEmail: string;

  constructor(
    public dialogRef: MatDialogRef<EditUserEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditUserEmailDialogModel,
    private formBuilder: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private uiAlertService: UiAlertService,
    private usersService: UsersService,
  ) {
    this.currentEmail = data.user.email;
    this.emailVerified = !!data.user.isVerifiedEmail;
    this.form = this.formBuilder.group({
      newEmail: new UntypedFormControl('', [Validators.required, Validators.email]),
    });
  }

  saveEmail(): void {
    if (this.form.invalid) {
      return;
    }
    this.saving = true;
    const newEmail = this.form.get('newEmail')?.value;
    this.usersService
      .updateEmail(this.data.user.id, newEmail)
      .then(() => {
        this.saving = false;
        this.currentEmail = newEmail;
        this.emailVerified = false;
        this.form.reset();
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: 'Email address has been successfully updated.',
        });
      })
      .catch((error: any) => {
        this.saving = false;
        let message =
          error?.error?.error ?? 'Unable to update the email address. Please try again.';
        if (error?.status === 409) {
          message = 'This email address is already in use. Please choose another.';
        }
        this.snackBar.openFromComponent(SnackBarComponent, { data: message });
      });
  }

  verifyEmail(): void {
    this.usersService
      .resendEmailValidation(this.data.user.id)
      .then(() => {
        this.uiAlertService
          .presentFieldInput(
            `Enter the verification code sent to the customer's email address.`,
            'Verification Code',
          )
          .then((value) => {
            if (value) {
              this.usersService.validateEmailCode(this.data.user.id, value).then((result) => {
                this.emailVerified = !!result;
                this.snackBar.openFromComponent(SnackBarComponent, {
                  data: result
                    ? 'Email address has been successfully verified.'
                    : 'Incorrect verification code.',
                });
              });
            }
          });
      })
      .catch((error: any) => {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: error?.error?.error ?? 'Unable to send the verification code. Please try again.',
        });
      });
  }

  forceVerifyEmail(): void {
    this.uiAlertService
      .presentAlertConfirm(
        "Are you sure you want to manually verify this customer's email address? Only proceed if you are certain the email belongs to the customer.",
      )
      .then((ok) => {
        if (ok) {
          this.usersService.verifyEmail(this.data.user.id).then(() => {
            this.emailVerified = true;
            this.snackBar.openFromComponent(SnackBarComponent, {
              data: 'The email address has been manually verified.',
            });
          });
        }
      });
  }

  onDismiss(): void {
    this.dialogRef.close();
  }
}

export class EditUserEmailDialogModel {
  constructor(public user: any) {}
}
